import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, ExternalLink, Check, Globe, Linkedin, BookOpen, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  fmtFrist, stageDot, recordRute, hentMedlemmer, patchEier, patchNeste, patchInfo,
  KONTAKT_STATUSER, SPEAKER_STAGES, PARTNER_STAGES, PRI_VALG,
  ROUTE_TYPES, BOOKING_STAGES, CONTRACT_STAGES, FIRMA_KATEGORIER, PARTNER_ROLLER, PARTNERSKAP_KATEGORIER, SPEAKER_ROLLER,
} from "./blokkData";

const TYPE_LABEL = { speaker: "Speaker", partner: "Partner", task: "Sak", kontakt: "Kontakt" };

export default function RecordDrawer({ rad, onLukk, refresh }) {
  const [medl, setMedl] = useState([]);
  const [tekst, setTekst] = useState(rad.nesteSteg || "");
  const [due, setDue] = useState(rad.frist || "");
  const [ask, setAsk] = useState(rad.ask || "");
  const [rute, setRute] = useState(rad.route || "");
  const [ruteType, setRuteType] = useState(rad.routeType || "");
  const [lagrer, setLagrer] = useState(false);
  const [beriker, setBeriker] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    hentMedlemmer().then(setMedl).catch(() => setMedl([]));
    const esc = (e) => { if (e.key === "Escape") onLukk(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onLukk]);

  useEffect(() => {
    setTekst(rad.nesteSteg || "");
    setDue(rad.frist || "");
    setAsk(rad.ask || "");
    setRute(rad.route || "");
    setRuteType(rad.routeType || "");
  }, [rad]);

  const settEier = async (id) => {
    try {
      await patchEier(rad, id);
      refresh();
      toast.success(id ? "Eier oppdatert" : "Eier fjernet");
    } catch { toast.error("Kunne ikke sette eier"); }
  };

  const settKontaktStatus = async (k) => {
    try {
      await api.patch(`/admin/os/kontakter/${rad.id}`, { status: k });
      refresh();
      toast.success("Status oppdatert");
    } catch { toast.error("Kunne ikke sette status"); }
  };

  const settStage = async (k) => {
    try {
      await patchInfo(rad, { stage: k });
      refresh();
      toast.success("Status oppdatert");
    } catch { toast.error("Kunne ikke sette status"); }
  };

  const settPri = async (p) => {
    try {
      await patchInfo(rad, { pri: rad.pri === p ? "" : p });
      refresh();
      toast.success("Prioritet oppdatert");
    } catch { toast.error("Kunne ikke sette prioritet"); }
  };

  const lagreAskRoute = async (e) => {
    e.preventDefault();
    setLagrer(true);
    try {
      await patchInfo(rad, { ask: ask.trim(), route: rute.trim(), routeType: ruteType });
      refresh();
      toast.success("Ask & route lagret");
    } catch { toast.error("Kunne ikke lagre"); } finally { setLagrer(false); }
  };

  const settFelt = async (felter, melding) => {
    try {
      await patchInfo(rad, felter);
      refresh();
      toast.success(melding);
    } catch { toast.error("Kunne ikke lagre"); }
  };

  const lagreNeste = async (e) => {
    e.preventDefault();
    setLagrer(true);
    try {
      await patchNeste(rad, tekst.trim(), due);
      refresh();
      toast.success("Next move lagret");
    } catch { toast.error("Kunne ikke lagre"); } finally { setLagrer(false); }
  };

  const stages = rad.type === "partner" ? PARTNER_STAGES : SPEAKER_STAGES;

  const erOrg = rad.type === "partner" || rad.targetType === "org";
  const berik = async () => {
    setBeriker(true);
    try {
      const { data } = await api.post("/admin/os/berik", { typ: rad.type === "partner" ? "partner" : "speaker", id: rad.id });
      const r = data.resultat || {};
      if (r.feil) toast.error(r.feil);
      else if (!r.funnet) toast.info("Fant ingen åpen profil på nettet for dette navnet");
      else toast.success("Profil oppdatert fra åpne kilder");
      refresh();
    } catch { toast.error("Kunne ikke hente profilinfo"); } finally { setBeriker(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid="dok-record-drawer">
      <div className="absolute inset-0 bg-black/25" onClick={onLukk} />
      <aside className="dok-rdrawer relative w-full max-w-[420px] h-full flex flex-col overflow-y-auto">
        <div className="dok-rdrawer-hode">
          {rad.bilde
            ? <span className={`dok-rdrawer-init foto${erOrg ? " org" : ""}`}><img src={rad.bilde} alt="" /></span>
            : <span className="dok-rdrawer-init">{(rad.navn || "?")[0]}</span>}
          <div className="flex-1 min-w-0">
            <p className="dok-rdrawer-navn" data-testid="dok-rdrawer-navn">{rad.navn}</p>
            <p className="dok-rdrawer-under">
              {[rad.rolleType || rad.under, TYPE_LABEL[rad.type]].filter(Boolean).join(" · ")}
            </p>
          </div>
          <button type="button" onClick={onLukk} className="dok-rdrawer-lukk" data-testid="dok-rdrawer-lukk"><X size={15} /></button>
        </div>

        <div className="px-5 pb-6 space-y-5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="dok-blokk-chip stor"><i className="dok-blokk-dot" style={{ background: stageDot(rad.stage) }} />{rad.stageLabel}</span>
            {rad.frist && <span className="dok-rad-due">Due {fmtFrist(rad.frist)}</span>}
            {rad.type === "partner" && rad.verdi > 0 && (
              <span className="dok-rdrawer-verdi">{Number(rad.verdi).toLocaleString("nb-NO")} NOK</span>
            )}
          </div>

          {(rad.type === "speaker" || rad.type === "partner") && (
            <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="dok-rdrawer-label">Status</p>
                <select value={rad.stage || ""} onChange={(e) => settStage(e.target.value)}
                  className="dok-rdrawer-input w-full" data-testid="dok-rdrawer-stage">
                  {stages.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div>
                <p className="dok-rdrawer-label">{rad.type === "partner" ? "Contract stage" : "Booking stage"}</p>
                <select value={rad.subStage || ""}
                  onChange={(e) => settFelt(rad.type === "partner" ? { contractStage: e.target.value } : { bookingStage: e.target.value }, "Sub-status lagret")}
                  className="dok-rdrawer-input w-full" data-testid="dok-rdrawer-substage">
                  {(rad.type === "partner" ? CONTRACT_STAGES : BOOKING_STAGES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
            </div>
            <div>
              <p className="dok-rdrawer-label">Priority</p>
              <div className="flex items-center gap-1 flex-wrap pt-0.5" data-testid="dok-rdrawer-prier">
                {PRI_VALG.map(([k, l]) => (
                  <button key={k} type="button" onClick={() => settPri(k)}
                    className={`dok-pri-knapp p-${k.toLowerCase()} ${rad.pri === k ? "aktiv" : ""}`}
                    data-testid={`dok-rdrawer-pri-${k}`}>{l}</button>
                ))}
              </div>
            </div>
            {rad.type === "partner" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="dok-rdrawer-label">Partnership category</p>
                  <select value={rad.partnerKategori || ""} onChange={(e) => settFelt({ partnershipCategory: e.target.value }, "Partnership category lagret")}
                    className="dok-rdrawer-input w-full" data-testid="dok-rdrawer-pkat">
                    <option value="">—</option>
                    {PARTNERSKAP_KATEGORIER.map((k) => <option key={k} value={k}>{k}</option>)}
                    {rad.partnerKategori && !PARTNERSKAP_KATEGORIER.includes(rad.partnerKategori) && <option value={rad.partnerKategori}>{rad.partnerKategori}</option>}
                  </select>
                </div>
                <div>
                  <p className="dok-rdrawer-label">Desired partner role</p>
                  <select value={rad.rolleType || ""} onChange={(e) => settFelt({ packageName: e.target.value }, "Partnerrolle lagret")}
                    className="dok-rdrawer-input w-full" data-testid="dok-rdrawer-rolle">
                    <option value="">—</option>
                    {PARTNER_ROLLER.map((k) => <option key={k} value={k}>{k}</option>)}
                    {rad.rolleType && !PARTNER_ROLLER.includes(rad.rolleType) && <option value={rad.rolleType}>{rad.rolleType}</option>}
                  </select>
                </div>
                <div>
                  <p className="dok-rdrawer-label">Company category</p>
                  <select value={rad.kategoriFirma || ""} onChange={(e) => settFelt({ companyCategory: e.target.value }, "Kategori lagret")}
                    className="dok-rdrawer-input w-full" data-testid="dok-rdrawer-kategori">
                    <option value="">—</option>
                    {FIRMA_KATEGORIER.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
                <div>
                  <p className="dok-rdrawer-label">Country</p>
                  <input defaultValue={rad.land || ""} placeholder="F.eks. Norge, USA"
                    onBlur={(e) => { const v = e.target.value.trim(); if (v !== (rad.land || "")) settFelt({ country: v }, "Country lagret"); }}
                    className="dok-rdrawer-input w-full" data-testid="dok-rdrawer-country" />
                </div>
              </div>
            )}
            {rad.type === "speaker" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="dok-rdrawer-label">Target type</p>
                  <select value={rad.targetType || "person"} onChange={(e) => settFelt({ targetType: e.target.value }, "Target type lagret")}
                    className="dok-rdrawer-input w-full" data-testid="dok-rdrawer-targettype">
                    <option value="person">Person</option>
                    <option value="org">Organisation</option>
                  </select>
                </div>
                <div>
                  <p className="dok-rdrawer-label">Geo</p>
                  <select value={rad.geo || ""} onChange={(e) => settFelt({ geo: e.target.value }, "Geo lagret")}
                    className="dok-rdrawer-input w-full" data-testid="dok-rdrawer-geo">
                    <option value="">—</option>
                    <option value="norge">Norge</option>
                    <option value="int">Internasjonalt</option>
                  </select>
                </div>
                <div>
                  <p className="dok-rdrawer-label">Role</p>
                  <select value={rad.rolleType || ""} onChange={(e) => settFelt({ roleLabel: e.target.value }, "Rolle lagret")}
                    className="dok-rdrawer-input w-full" data-testid="dok-rdrawer-srolle">
                    <option value="">—</option>
                    {SPEAKER_ROLLER.map((k) => <option key={k} value={k}>{k}</option>)}
                    {rad.rolleType && !SPEAKER_ROLLER.includes(rad.rolleType) && <option value={rad.rolleType}>{rad.rolleType}</option>}
                  </select>
                </div>
                {rad.targetType === "org" && (
                  <div>
                    <p className="dok-rdrawer-label">Target person</p>
                    <input defaultValue={rad.targetPerson || ""} placeholder="Not identified"
                      onBlur={(e) => { const v = e.target.value.trim(); if (v !== (rad.targetPerson || "")) settFelt({ targetPerson: v }, "Target person lagret"); }}
                      className="dok-rdrawer-input w-full" data-testid="dok-rdrawer-targetperson" />
                  </div>
                )}
              </div>
            )}
            </>
          )}

          {rad.type === "kontakt" && (
            <div>
              <p className="dok-rdrawer-label">Status</p>
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="dok-rdrawer-statuser">
                {KONTAKT_STATUSER.map(([k, l]) => (
                  <button key={k} type="button" onClick={() => settKontaktStatus(k)}
                    className={`dok-strip-chip ${rad.stage === k ? "aktiv" : ""}`}
                    data-testid={`dok-rdrawer-status-${k}`}>
                    {l}{rad.stage === k && <Check size={11} />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(rad.type === "speaker" || rad.type === "partner") && (
            <div>
              <p className="dok-rdrawer-label">Profil</p>
              <div className="dok-profil-lenker" data-testid="dok-rdrawer-profil">
                {rad.web && <a href={rad.web} target="_blank" rel="noreferrer" data-testid="dok-profil-web"><Globe size={11} /> Nettside</a>}
                {rad.linkedin && <a href={rad.linkedin} target="_blank" rel="noreferrer" data-testid="dok-profil-linkedin"><Linkedin size={11} /> LinkedIn</a>}
                {rad.wikipedia && <a href={rad.wikipedia} target="_blank" rel="noreferrer" data-testid="dok-profil-wikipedia"><BookOpen size={11} /> Wikipedia</a>}
                <button type="button" onClick={berik} disabled={beriker} data-testid="dok-rdrawer-berik">
                  <RefreshCw size={11} className={beriker ? "dok-spinn" : ""} /> {beriker ? "Henter …" : "Hent fra nettet"}
                </button>
              </div>
            </div>
          )}

          {rad.type !== "task" && (
            <form onSubmit={lagreAskRoute}>
              <p className="dok-rdrawer-label">Ask / objective</p>
              <input value={ask} onChange={(e) => setAsk(e.target.value)} placeholder="Hva ber vi om? F.eks. «Keynote 13 Apr + fee»"
                className="dok-rdrawer-input" data-testid="dok-rdrawer-ask" />
              <p className="dok-rdrawer-label mt-3">Contact route — har vi en vei inn?</p>
              <div className="flex items-center gap-2">
                <select value={ruteType} onChange={(e) => setRuteType(e.target.value)}
                  className="dok-rdrawer-input" data-testid="dok-rdrawer-routetype">
                  {ROUTE_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <input value={rute} onChange={(e) => setRute(e.target.value)} placeholder="Via hvem / notat"
                  className="dok-rdrawer-input flex-1" data-testid="dok-rdrawer-route" />
                <button type="submit" className="dok-rdrawer-lagre" disabled={lagrer} data-testid="dok-rdrawer-askroute-lagre">
                  {lagrer ? "…" : "Lagre"}
                </button>
              </div>
            </form>
          )}

          {rad.maal && (
            <div>
              <p className="dok-rdrawer-label">Objective i dette dokumentet</p>
              <p className="dok-rdrawer-tekst">{rad.maal}</p>
            </div>
          )}

          <div>
            <p className="dok-rdrawer-label">Owner</p>
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="dok-rdrawer-eiere">
              {medl.map((m) => (
                <button key={m.id} type="button" onClick={() => settEier(rad.eierId === m.id ? null : m.id)}
                  aria-pressed={rad.eierId === m.id}
                  className={`dok-strip-chip ${rad.eierId === m.id ? "aktiv" : ""}`}
                  data-testid={`dok-rdrawer-eier-${m.id}`}>
                  <i style={{ background: m.color }}>{(m.name || "?")[0]}</i>{m.name}
                  {m.status === "invitert" && <span className="inv">invitert</span>}
                  {rad.eierId === m.id && <Check size={11} />}
                </button>
              ))}
              {!rad.eierId && <span className="dok-eier-chip uten"><i className="ut">!</i>Unassigned</span>}
            </div>
          </div>

          {rad.type !== "task" && (
            <form onSubmit={lagreNeste}>
              <p className="dok-rdrawer-label">Next move</p>
              <input value={tekst} onChange={(e) => setTekst(e.target.value)} placeholder="Hva er neste konkrete trekk?"
                className="dok-rdrawer-input" data-testid="dok-rdrawer-next" />
              <div className="flex items-center gap-2 mt-2">
                <input type="date" value={due} onChange={(e) => setDue(e.target.value)}
                  className="dok-rdrawer-input flex-1" data-testid="dok-rdrawer-due" />
                <button type="submit" className="dok-rdrawer-lagre" disabled={lagrer} data-testid="dok-rdrawer-lagre">
                  {lagrer ? "Lagrer …" : "Lagre"}
                </button>
              </div>
            </form>
          )}

          {rad.type === "task" && rad.nesteSteg && (
            <div>
              <p className="dok-rdrawer-label">Neste delsteg</p>
              <p className="dok-rdrawer-tekst">{rad.nesteSteg}</p>
            </div>
          )}

          {rad.type !== "kontakt" && (
            <button type="button" onClick={() => nav(recordRute(rad))} className="dok-rdrawer-aapne" data-testid="dok-rdrawer-aapne">
              <ExternalLink size={12} /> Åpne i {rad.type === "task" ? "Saker" : rad.type === "partner" ? "Partnere" : "Speakers"}
            </button>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
}
